// utils/urlSecrets.ts
//
// One-time secrets ride in some URLs: email links (`?token=` on reset,
// verify and unsubscribe pages, `/verify-email/<token>`), native auth returns
// (`ott`, `tfa`) and OAuth callbacks (`code`). ORA-1089: the pages that read
// one drop it from the address bar, and analytics and error reports scrub
// them from every URL they record.

/** Query parameters whose value is a credential. */
export const URL_SECRET_PARAMS: readonly string[] = [
  'token',
  'ott',
  'tfa',
  'code',
];

/** A path whose last segment is a credential: `/verify-email/<token>`. */
const SECRET_PATH = /^(\/verify-email\/)[^/]+/;
const REDACTED_SEGMENT = 'redacted';
/** Resolves relative URLs; never appears in a scrubbed result. */
const PLACEHOLDER_ORIGIN = 'http://scrub.invalid';

const hasScheme = (value: string): boolean =>
  /^[a-z][a-z0-9+.-]*:/i.test(value);

/**
 * `value` without its secret query parameters and with a secret path segment
 * redacted. Takes an absolute URL, a path, or anything else (returned as it
 * was when it holds no secret or is not a URL).
 */
export const scrubUrlSecrets = (value: string): string => {
  if (!value) return value;

  let url: URL;
  try {
    url = new URL(value, PLACEHOLDER_ORIGIN);
  } catch {
    return value;
  }

  let isChanged = false;
  for (const param of URL_SECRET_PARAMS) {
    if (url.searchParams.has(param)) {
      url.searchParams.delete(param);
      isChanged = true;
    }
  }

  const pathname = url.pathname.replace(SECRET_PATH, `$1${REDACTED_SEGMENT}`);
  if (pathname !== url.pathname) {
    url.pathname = pathname;
    isChanged = true;
  }

  if (!isChanged) return value;
  return hasScheme(value)
    ? url.toString()
    : `${url.pathname}${url.search}${url.hash}`;
};

/**
 * `text` with every URL-like word in it scrubbed: a span description
 * (`GET https://…?token=…`) or a log line carries URLs among other words.
 */
export const scrubUrlSecretsInText = (text: string): string =>
  text.replace(/\S+/g, (word) =>
    word.includes('?') || SECRET_PATH_ANYWHERE.test(word)
      ? scrubUrlSecrets(word)
      : word
  );

const SECRET_PATH_ANYWHERE = /\/verify-email\/[^/?#\s]+/;
/** How deep the nested scrubs below look (event payloads are shallow). */
const MAX_SCRUB_DEPTH = 6;

/**
 * Every string property named in `keys`, at any depth of `value` (objects
 * and arrays), scrubbed in place.
 */
export const scrubUrlPropertiesDeep = (
  value: unknown,
  keys: readonly string[],
  depth = 0
): void => {
  if (!value || typeof value !== 'object' || depth > MAX_SCRUB_DEPTH) return;

  if (Array.isArray(value)) {
    value.forEach((item) => scrubUrlPropertiesDeep(item, keys, depth + 1));
    return;
  }

  const record = value as Record<string, unknown>;
  for (const [key, item] of Object.entries(record)) {
    if (typeof item === 'string') {
      if (keys.includes(key)) record[key] = scrubUrlSecrets(item);
    } else {
      scrubUrlPropertiesDeep(item, keys, depth + 1);
    }
  }
};

/** Every string at any depth of `value`, URL-scrubbed in place as text. */
export const scrubStringsDeep = (value: unknown, depth = 0): void => {
  if (!value || typeof value !== 'object' || depth > MAX_SCRUB_DEPTH) return;

  const record = value as Record<string, unknown>;
  for (const [key, item] of Object.entries(record)) {
    if (typeof item === 'string') record[key] = scrubUrlSecretsInText(item);
    else scrubStringsDeep(item, depth + 1);
  }
};

/** Every string property among `keys` of `record`, scrubbed in place. */
export const scrubUrlProperties = (
  record: Record<string, unknown> | null | undefined,
  keys: readonly string[]
): void => {
  if (!record) return;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string') record[key] = scrubUrlSecrets(value);
  }
};

/** A location's parts, as the router's `useLocation()` gives them. */
interface LocationParts {
  pathname: string;
  search: string;
  hash: string;
}

/**
 * `location` without `params` in its query, for a `navigate(…, { replace:
 * true })` that drops a secret from the address bar and from the router's
 * own location alike. Every other part is kept.
 */
export const withoutParams = (
  location: LocationParts,
  params: readonly string[]
): LocationParts => {
  const search = new URLSearchParams(location.search);
  params.forEach((param) => search.delete(param));
  const query = search.toString();

  return {
    pathname: location.pathname,
    search: query ? `?${query}` : '',
    hash: location.hash,
  };
};
